/**
 * @file 学年と学校年度の計算 (受験のスケジュール用)。
 *
 * @what: 日付がどの学年 (小4など) に当たるかを、試験日の学年 (ルートの exam_grade) から数える。学習者の
 *   生年月日と試験の学年・月日から試験日を出す。
 * @why: 「2033年4月」だけでは遠くて分かりにくいので学年を添える。試験日は「小6の2月1日」と決まっているので
 *   生年月日から出し、手で直せるようにする (代表の要望、2026-09-05)。
 * @failure: 学年の通し番号が1〜12の外 (就学前・卒業後) は null を返し、表示側は学年を省く。日付は
 *   YYYY-MM-DD を前提にし、それ以外では NaN 入りの結果になる (呼び手が検証済みの日付を渡す)。
 */

/** 学年の通し番号の下限 (小1) と上限 (高3)。backend の StudyTextbookRouteExamGradeMax (12) と同じ値にする。 */
export const GRADE_INDEX_MIN = 1;
export const GRADE_INDEX_MAX = 12;

/** 学年の通し番号 (小1=1 … 小6=6、中1=7 … 中3=9、高1=10 … 高3=12) を表示名にする。範囲外は null になる。 */
export function gradeLabel(index: number): string | null {
  if (!Number.isInteger(index) || index < GRADE_INDEX_MIN || index > GRADE_INDEX_MAX) return null;
  if (index <= 6) return `小${index}`;
  if (index <= 9) return `中${index - 6}`;
  return `高${index - 9}`;
}

/** 日付が属する学校年度 (4月始まり) の西暦を返す。1〜3月は前の年度になる。 */
export function schoolYearOf(iso: string): number {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  return m >= 4 ? y : y - 1;
}

/** 試験日が examGrade の学年に当たるとして、date の学年の通し番号を返す。 */
export function gradeIndexAt(date: string, examDate: string, examGrade: number): number {
  return examGrade - (schoolYearOf(examDate) - schoolYearOf(date));
}

/** gradeIndexAt の表示名で、範囲外なら null になる。 */
export function gradeLabelAt(date: string, examDate: string, examGrade: number): string | null {
  return gradeLabel(gradeIndexAt(date, examDate, examGrade));
}

/**
 * 生年月日から試験日を出す。
 *
 * @what: 4月2日〜翌年4月1日生まれが同じ学年になる規則で小1の年度を求め、examGrade の学年の年度の中の
 *   monthDay (試験の月日) を返す。1〜3月の試験は年度の翌年になる。
 * @why: 中学入試は小6の2月1日、国立大の前期は高3の2月25日と決まっているので、生年月日だけで試験日が
 *   決まる。
 */
export function examDateFromBirth(birthDate: string, examGrade: number, monthDay: string): string {
  const schoolYear = entrySchoolYear(birthDate) + examGrade - 1;
  const month = Number(monthDay.slice(0, 2));
  const calYear = month >= 4 ? schoolYear : schoolYear + 1;
  return `${calYear}-${monthDay}`;
}

/** 試験日の月日 ("02-01") を「2月1日」で出す。 */
export function formatMonthDay(monthDay: string): string {
  return `${Number(monthDay.slice(0, 2))}月${Number(monthDay.slice(3, 5))}日`;
}

/** 生年月日から小1の年度 (4月2日〜翌年4月1日生まれが同じ学年) を返す。 */
export function entrySchoolYear(birthDate: string): number {
  const y = Number(birthDate.slice(0, 4));
  return birthDate.slice(5, 10) <= "04-01" ? y + 6 : y + 7;
}

/**
 * 生年月日から、date の学年の表示名を返す。就学前と高3のあと (おとなの学び直しなど) は null になる。
 * ※なぜなら学年は学習者の生年月日で決まるもので、試験の学年から逆算するとおとなにも「高3」が付くためである。
 */
export function gradeLabelFromBirth(date: string, birthDate: string): string | null {
  return gradeLabel(schoolYearOf(date) - entrySchoolYear(birthDate) + 1);
}
